import { useEffect } from "react";
import MarketingShell, { MK } from "./MarketingShell";

/**
 * Support — for existing Ratezy businesses. Offers two clearly clickable
 * options: email and WhatsApp. Visually consistent with the marketing site.
 */
const SUPPORT_EMAIL = "support@ratezy.com.au";
const WHATSAPP_NUMBER = "+61 431 907 078";
const WHATSAPP_LINK = "https://wa.me/61431907078";

export default function Support() {
  useEffect(() => {
    document.title = "Support — Ratezy";
  }, []);

  return (
    <MarketingShell>
      <section style={{ padding: "clamp(56px, 10vw, 120px) 0 clamp(56px, 8vw, 100px)" }}>
        <div style={{ ...MK.wrap, maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <div style={MK.eyebrow}>Support</div>
            <h1 style={MK.h1}>How can we help?</h1>
            <p style={{ ...MK.lead, maxWidth: 620, margin: "18px auto 0" }}>
              Already using Ratezy? Choose the option that suits you best — we'll
              get you back up and running quickly.
            </p>
          </div>

          <div style={{ display: "grid", gap: 22, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            {/* Email support */}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="support-email-card"
              style={{ ...MK.card, textDecoration: "none", display: "block", transition: "transform 0.2s ease, border-color 0.2s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = MK.color.blue500; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = MK.color.border; }}
            >
              <IconTile bg="rgba(46,125,255,0.14)" stroke={MK.color.blue400}>
                <path d="M4 6h16v12H4z" />
                <path d="M4 6l8 7 8-7" />
              </IconTile>
              <div style={MK.cardTitle}>Email Support</div>
              <div style={{ color: MK.color.blue300, fontFamily: MK.fonts.mono, fontSize: "0.92rem", marginTop: 8, wordBreak: "break-all" }}>
                {SUPPORT_EMAIL}
              </div>
              <p style={{ color: MK.color.grey400, marginTop: 12, fontSize: "0.94rem" }}>
                For account, billing, setup and technical issues. We typically reply within one business day.
              </p>
              <div style={{ ...MK.btnGhost, marginTop: 20, display: "inline-flex" }}>
                Email us →
              </div>
            </a>

            {/* WhatsApp support */}
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="support-whatsapp-card"
              style={{ ...MK.card, textDecoration: "none", display: "block", transition: "transform 0.2s ease, border-color 0.2s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = "#37c983"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = MK.color.border; }}
            >
              <IconTile bg="rgba(55,201,131,0.14)" stroke="#37c983">
                <path d="M20 12.06a8 8 0 1 1-3.42-6.55L20 4l-1.5 3.42A7.96 7.96 0 0 1 20 12.06z" />
                <path d="M15.5 14.5c-.6.6-1.5.7-2.3.3-1.2-.6-2.3-1.7-2.9-2.9-.4-.8-.3-1.7.3-2.3l.6-.6-1.5-2.3-1 .3c-.5.2-.8.7-.9 1.2-.3 2.4 1.5 5.4 3.9 6.6 2.1 1 4 .6 4.6-.2.3-.4.4-.9.2-1.4l-.3-.9-.7.2z" />
              </IconTile>
              <div style={MK.cardTitle}>WhatsApp Support</div>
              <div style={{ color: "#8ee2b5", fontFamily: MK.fonts.mono, fontSize: "0.92rem", marginTop: 8 }}>
                {WHATSAPP_NUMBER}
              </div>
              <p style={{ color: MK.color.grey400, marginTop: 12, fontSize: "0.94rem" }}>
                For quick questions and troubleshooting. Chat with us during business hours (AEST).
              </p>
              <div style={{ ...MK.btnGhost, marginTop: 20, display: "inline-flex", borderColor: "rgba(55,201,131,0.5)", color: "#8ee2b5" }}>
                Open WhatsApp →
              </div>
            </a>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

function IconTile({ bg, stroke, children }) {
  return (
    <div style={{
      width: 48, height: 48, borderRadius: 12, background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      marginBottom: 20,
    }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </svg>
    </div>
  );
}
